// Platform super-admin API client — SEPARATE from api/client.ts.
//
// This is deliberately its own fetch wrapper, not a thin variant of the
// tenant apiFetch: the tenant client's handle401() hard-redirects to
// `/login` on any 401, which would be WRONG here (redirecting a stray
// tenant-agent page load to the platform login, or a platform 401 leaking
// into a tenant-facing navigation). PlatformAuthProvider (and therefore this
// client) is only ever mounted under `/super_admin/*` routes, so a 401 here
// can only ever mean "not authenticated as the platform admin, on a
// platform route" — redirecting to `/super_admin/login` is always correct.
//
// Auth transport: httpOnly 'platform_session' cookie, managed by the backend
// (see dashboard/src/middleware/platformAuth.ts). Never read/written directly.

import { ApiError } from './client';

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

function handlePlatform401(): never {
  if (window.location.pathname !== '/super_admin/login') {
    window.location.href = '/super_admin/login';
  }
  throw new Error('Unauthorized');
}

export async function platformApiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const hasBody = init.body !== undefined && init.body !== null;
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers as Record<string, string> | undefined),
    },
  });

  if (res.status === 401) handlePlatform401();

  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { body = null; }
    throw new ApiError(res.status, body);
  }

  return res.json() as Promise<T>;
}

export { ApiError };
